import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { directoryCategories, pricingModelLabel, productCategoryLabel } from '../../../../constants/advise';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { LoadError } from '../LoadError';
import { SupplierFormDialog } from '../SupplierFormDialog';
import { useEngagementEditor } from './EngagementEditorContext';
import { PanelHeader } from './PanelHeader';
import { supplierKey } from './supplierKey';
import { categoriesFirst, SupplierSort, SupplierSortField, visibleSuppliers } from './SupplierSelectionPanel.calc';
import { SupplierCategoriesCell } from './SupplierCategoriesCell';

// Set widths, with Category taking the rest, so expanding a row's categories never reflows the columns.
const COLUMNS: { field: SupplierSortField; label: string; sortLabel: string; width?: string }[] = [
  { field: 'supplier_name', label: 'Vendor', sortLabel: 'Name', width: '22%' },
  { field: 'location', label: 'Location', sortLabel: 'Location', width: '18%' },
  { field: 'pricing_model', label: 'Pricing', sortLabel: 'Pricing', width: '14%' },
  { field: 'categories', label: 'Category', sortLabel: 'Category' }
];

// Every Salesforce supplier is listed, well over a thousand, so only a page of rows renders at a time.
const PAGE_SIZE = 50;

interface SupplierSelectionPanelProps {
  onReviewFeatureInput: () => void;
}

/** Select Suppliers to Compare: the engagement's shortlist, picked from the Salesforce and Advise suppliers. */
export const SupplierSelectionPanel = ({ onReviewFeatureInput }: SupplierSelectionPanelProps) => {
  const { canEdit, supplierSelection } = useEngagementEditor();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [sort, setSort] = useState<SupplierSort>({ field: 'supplier_name', direction: 'asc' });
  const [adding, setAdding] = useState(false);
  const [page, setPage] = useState(0);
  const [onlySelected, setOnlySelected] = useState(false);
  const directory = supplierSelection?.directory ?? null;
  const load = supplierSelection?.load;

  useEffect(() => {
    if (!directory) load?.();
  }, [directory, load]);

  const matching = useMemo(
    () => (directory ? visibleSuppliers(directory, { search, category, sort }) : []),
    [directory, search, category, sort]
  );
  const categories = useMemo(() => directoryCategories(directory ?? []), [directory]);

  if (!supplierSelection) return null;
  const { failed, selectedKeys, select, saveSupplier } = supplierSelection;

  if (!directory) {
    if (failed) {
      return (
        <LoadError
          subject="the suppliers"
          consequence="there is nothing to pick from yet"
          onRetry={load}
        />
      );
    }
    return <ComponentSpinner sx={{ padding: '3rem' }} />;
  }

  const selected = new Set(selectedKeys);
  // The picks sit wherever the sort puts them, mostly on other pages, so this shows them together.
  const rows = onlySelected ? matching.filter((row) => selected.has(supplierKey(row))) : matching;
  const showSelected = (show: boolean) => {
    setPage(0);
    setOnlySelected(show);
  };
  const shownPage = Math.min(page, Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1));
  const pageRows = rows.slice(shownPage * PAGE_SIZE, (shownPage + 1) * PAGE_SIZE);
  const shownKeys = pageRows.map(supplierKey);
  const shownSelected = shownKeys.filter((key) => selected.has(key)).length;
  const sortBy = (field: SupplierSortField) => {
    setPage(0);
    setSort((prev) => ({ field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  return (
    <Box className="flex flex-col gap-3">
      {failed && (
        <LoadError
          subject="the suppliers"
          consequence="the list may be out of date"
          onRetry={load}
        />
      )}
      <Box className="flex items-start justify-between gap-4">
        <PanelHeader
          title="Select Suppliers to Compare"
          description={`Suppliers — ${directory.length} vendors available`}
        />
        <Box className="flex flex-col items-end gap-1">
          <Chip
            label={`View Shortlisted Suppliers (${selected.size})`}
            color="primary"
            size="small"
            variant={onlySelected ? 'filled' : 'outlined'}
            disabled={!onlySelected && selected.size === 0}
            aria-pressed={onlySelected}
            onClick={() => showSelected(!onlySelected)}
            onDelete={onlySelected ? () => showSelected(false) : undefined}
          />
          <TextButton
            disabled={selected.size === 0}
            onClick={onReviewFeatureInput}>
            Review Supplier Feature Input →
          </TextButton>
        </Box>
      </Box>

      <Box className="flex flex-wrap items-center gap-2">
        <TextField
          size="small"
          placeholder="Search vendors…"
          value={search}
          onChange={(event) => {
            setPage(0);
            setSearch(event.target.value);
          }}
          inputProps={{ 'aria-label': 'Search vendors' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <TextField
          select
          size="small"
          label="Category"
          sx={{ minWidth: 224 }}
          value={category ?? ''}
          onChange={(event) => {
            setPage(0);
            setCategory(event.target.value || null);
          }}>
          {categories.map((option) => (
            <MenuItem
              key={option}
              value={option}>
              {productCategoryLabel(option)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Sort"
          sx={{ minWidth: 128 }}
          value={sort.field}
          onChange={(event) => {
            setPage(0);
            setSort({ field: event.target.value as SupplierSortField, direction: 'asc' });
          }}>
          {COLUMNS.map((column) => (
            <MenuItem
              key={column.field}
              value={column.field}>
              {column.sortLabel}
            </MenuItem>
          ))}
        </TextField>
        {category && (
          <Chip
            label={productCategoryLabel(category)}
            color="primary"
            variant="outlined"
            onDelete={() => {
              setPage(0);
              setCategory(null);
            }}
          />
        )}
        <Box className="ml-auto flex items-center gap-3">
          {canEdit && (
            <button
              type="button"
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              onClick={() => setAdding(true)}>
              + Add Supplier
            </button>
          )}
          <TextSpan
            size="sm"
            color="grey6">
            Showing {rows.length} of {directory.length}
          </TextSpan>
        </Box>
      </Box>

      <TableContainer className="max-h-[60vh]">
        <Table
          stickyHeader
          size="small"
          sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 56 }} />
            {COLUMNS.map((column) => (
              <col
                key={column.field}
                style={{ width: column.width }}
              />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  disabled={!canEdit || pageRows.length === 0}
                  checked={pageRows.length > 0 && shownSelected === pageRows.length}
                  indeterminate={shownSelected > 0 && shownSelected < pageRows.length}
                  onChange={(event) => select(shownKeys, event.target.checked)}
                  inputProps={{ 'aria-label': 'Select every vendor shown' }}
                />
              </TableCell>
              {COLUMNS.map((column) => (
                <TableCell key={column.field}>
                  <TableSortLabel
                    active={sort.field === column.field}
                    direction={sort.field === column.field ? sort.direction : 'asc'}
                    onClick={() => sortBy(column.field)}>
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1}>
                  <TextBlock
                    size="sm"
                    color="grey6">
                    No vendors match.
                  </TextBlock>
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((row) => {
              const key = supplierKey(row);
              const isSelected = selected.has(key);
              return (
                <TableRow
                  key={key}
                  hover
                  selected={isSelected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      disabled={!canEdit}
                      checked={isSelected}
                      onChange={(event) => select([key], event.target.checked)}
                      inputProps={{ 'aria-label': `Select ${row.supplier_name}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextSpan
                      size="sm"
                      weight={isSelected ? 'semibold' : 'normal'}>
                      {row.supplier_name}
                    </TextSpan>
                  </TableCell>
                  <TableCell>
                    <TextSpan
                      size="sm"
                      color="grey7">
                      {row.location}
                    </TextSpan>
                  </TableCell>
                  <TableCell>
                    {row.pricing_model && (
                      <Chip
                        label={pricingModelLabel(row.pricing_model)}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <SupplierCategoriesCell categories={categoriesFirst(row.categories, category)} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={rows.length}
          page={shownPage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[]}
          onPageChange={(_event, next) => setPage(next)}
        />
      )}

      {adding && (
        <SupplierFormDialog
          open
          onClose={() => setAdding(false)}
          onSave={saveSupplier}
          directory={directory}
        />
      )}
    </Box>
  );
};
